import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight, Phone, Mail } from "lucide-react";
import { BUSINESS } from "@/data/content";
import { LEGAL_PAGES } from "@/data/legal";
import useSEO from "@/hooks/useSEO";
import { breadcrumbSchema } from "@/lib/schema";

export default function LegalPage({ page = "privacy-policy" }) {
  const doc = LEGAL_PAGES[page];

  useSEO({
    title: doc.metaTitle,
    description: doc.metaDesc,
    jsonld: [
      breadcrumbSchema([
        { name: "Home", href: "/" },
        { name: doc.crumb, href: `/${doc.slug}` },
      ]),
    ],
  });

  return (
    <div data-testid={`legal-page-${doc.slug}`}>
      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-16 overflow-hidden grain">
        <div className="pointer-events-none absolute -top-40 -right-20 w-[520px] h-[520px] bg-[#E60000]/10 blur-[120px] rounded-full" />
        <div className="mx-auto max-w-[1000px] px-6 md:px-10 lg:px-16">
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/50 mb-8 em-rise">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{doc.crumb}</span>
          </div>
          <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-4 em-rise">
            {doc.kicker}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-[72px] leading-[0.95] tracking-tighter font-black em-rise" style={{ animationDelay: "80ms" }}>
            {doc.h1A}{" "}
            <span className="text-white/40">{doc.h1B}</span>
          </h1>
          <p className="mt-4 text-xs tracking-[0.2em] uppercase text-white/45 em-rise" style={{ animationDelay: "140ms" }}>
            Last updated · {doc.updated}
          </p>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed em-rise" style={{ animationDelay: "200ms" }}>
            {doc.intro}
          </p>
        </div>
      </section>

      {/* Sections */}
      <section className="py-20 lg:py-28 bg-[#F4F4F5] text-[#0A0A0A] border-t border-[#E4E4E7]">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10 lg:px-16 space-y-14">
          {doc.sections.map((s, i) => (
            <article key={i} data-testid={`legal-section-${i}`} className="border-l-2 border-[#E60000] pl-6 md:pl-10">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#E60000] font-bold mb-3">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h2 className="font-display text-2xl md:text-3xl tracking-tighter font-bold leading-tight">
                {s.h}
              </h2>
              {s.body && (
                <div className="mt-4 space-y-4 text-sm md:text-base text-black/75 leading-relaxed">
                  {s.body.map((p, j) => <p key={j}>{p}</p>)}
                </div>
              )}
              {s.items && (
                <ul className="mt-5 space-y-2.5">
                  {s.items.map((it, j) => (
                    <li key={j} className="flex gap-3 text-sm md:text-base text-black/75 leading-relaxed">
                      <span className="text-[#E60000] mt-1 shrink-0">▪</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* Contact strip */}
      <section className="py-16 border-t border-[#262626] bg-[#0A0A0A]">
        <div className="mx-auto max-w-[1000px] px-6 md:px-10 lg:px-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-2">— Questions about this page?</div>
            <p className="font-display text-2xl md:text-3xl font-bold tracking-tighter text-white">
              Talk to a human, not a legal department.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={`mailto:${BUSINESS.email}`}
              data-testid="legal-email-cta"
              className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold px-6 py-4 text-sm transition-colors"
            >
              <Mail className="w-4 h-4" /> {BUSINESS.email}
            </a>
            <a
              href={BUSINESS.phoneHref}
              data-testid="legal-call-cta"
              className="inline-flex items-center gap-2 border border-[#262626] hover:border-white text-white font-bold px-6 py-4 text-sm transition-colors"
            >
              <Phone className="w-4 h-4" /> {BUSINESS.phone}
            </a>
            <Link
              to="/"
              data-testid="legal-home-link"
              className="inline-flex items-center gap-2 border border-[#262626] hover:border-white text-white font-bold px-6 py-4 text-sm transition-colors"
            >
              Back to Home <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
